import { ClosingContainer } from "../utils/ui/ClosingContainer";
import { FieldSet } from "../utils/ui/FieldSet";
import { SelectInput } from "../utils/ui/SelectInput";
import { DropDownInput } from "../utils/ui/DropDownInput";
import { Scene } from "../scene/Scene";
import { Config } from "../config/Config";
import { ScenePanel } from "./ScenePanel";
import { LeanToFieldSet } from "./leanto/LeanToFieldSet";
import { InsetBayFieldSet } from "./InsetBayFieldSet";
import { Building, WallSide } from "../building/Building";
import { ISceneAddon } from "../scene/addon/ISceneAddon";
import { Main } from "../Main";
import { WallAddon } from "../scene/addon/WallAddon";
import { PostManager } from "../building/post/PostManager";
import { RidgeVent } from "../scene/extras/RidgeVent";
import { IInput } from "../utils/ui/Input";
import { Field } from "../utils/ui/Field";
import { WarningWindow } from "../ui/WarningWindow";
import { ConfirmWindow } from "../ui/ConfirmWindow";
import {CustomPopupWindow} from "ui/CustomPopupWindow";
import { BooleanInput } from "../utils/ui/BooleanInput";
import { NumberInput } from "../utils/ui/NumberInput";
import {LeanToSetting} from "./leanto/LeanToSetting";

export enum CheckAddonType
{
	HEIGHT = 1,
	LENGTH = 2,
	WIDTH = 3,
}

export class ToolsView
{
	private _properties:HTMLElement;
	private _fieldsContainer:HTMLElement;

	private _closingContainers:ClosingContainer[];

	// FieldSets

	private _buildingSize     : FieldSet;
	private _overHang         : FieldSet;
	private _colors           : FieldSet;
	private _ridgeVent        : FieldSet;
	private _ridgeVentText    : HTMLElement;
	private _insulation       : FieldSet;
	private _translucentPanels: FieldSet;
	private _wallLinerPanels  : FieldSet;
	private _wainscot         : FieldSet;
	private _insetBay         : InsetBayFieldSet;
	private _leanTo           : LeanToFieldSet;
	private _gutter           : FieldSet;
	private _supportBeam      : FieldSet;

	private _singleSlope      : SelectInput<any>;

	private _ridgeVentType    : SelectInput<any>;
	private _ridgeVentMode    : BooleanInput;
	private _wainscotInput    : BooleanInput;
	private _gutterInput      : BooleanInput;
	private _supportBeamInput : BooleanInput;

	private _colorWall        : DropDownInput<string>;
	private _colorRoof        : DropDownInput<string>;
	private _colorTrim        : DropDownInput<string>;
	private _colorRollUp      : DropDownInput<string>;
	private _colorRidgeVent   : DropDownInput<string>;
	private _colorWainscot    : DropDownInput<string>;

	private _scenePanel       : ScenePanel;

	private _scene            : Scene;
	private _config           : Config;

	private _lastHeightValue  : number;
	private _lastLengthValue  : number;
	private _lastWidthValue   : number;

	constructor(scene: Scene, config: Config)
	{
		this._scene = scene;
		this._config = config;
		this._properties = document.getElementById("properties");
		this._fieldsContainer = document.getElementById("properties_fields");
		this.init();
	}

	public setLastValues()
	{
		const building = this._config.building;
		this._lastHeightValue = building.height.value;
		this._lastLengthValue = building.length.value;
		this._lastWidthValue = building.width.value;
	}

	private init(): void
	{
		this.setLastValues();

		this._fieldsContainer.innerHTML = "";

		this._closingContainers = [];

		this.createBuildingSize();
		this.createOverhangSection();
		this.createLeanToSection();
		//this.createInsetBaySection();
		this.createAddons();
		this.createWainscotSection();
		this.createGutterSection();
		this.createRidgeVentSection();
		this.createColorSelection();
		this.createInsulationSection();
		this.createTranslucentPanelsSection();
		this.createWallLinerPanelsSection();
		//this.createSupportBeamSection();
		
		this.addListeners();

		this._fieldsContainer.addEventListener("click", this.onClick);

		this.initScrollFix();
	}

	public addListeners()
	{
		this._config.building.width.signal.add(this.checkForAddonsForWidthChange);
		this._config.building.length.signal.add(this.checkForAddonsForLengthChange);
		this._config.building.height.signal.add(this.checkForAddonsForHeightChange);
		this._config.building.single_slope.signal.add(this.checkForAddonsForSingleSlopeChange);

		this._config.building.single_slope.signal.add(this.checkForMaximumHeight);
		this._config.building.pitch.signal.add(this.checkForMaximumHeight);
		this._config.building.height.signal.add(this.checkForMaximumHeight);
		this._config.building.width.signal.add(this.checkForMaximumHeight);

		this._config.building.leanto.front.cut_back.signal.add(this.checkForAddonsForLeantoCutChangeFrontOrBack);
		this._config.building.leanto.front.cut_front.signal.add(this.checkForAddonsForLeantoCutChangeFrontOrBack);
		this._config.building.leanto.back.cut_back.signal.add(this.checkForAddonsForLeantoCutChangeFrontOrBack);
		this._config.building.leanto.back.cut_front.signal.add(this.checkForAddonsForLeantoCutChangeFrontOrBack);

		this._config.building.leanto.left.cut_back.signal.add(this.checkForAddonsForLeantoCutChangeLeftOrRight);
		this._config.building.leanto.left.cut_front.signal.add(this.checkForAddonsForLeantoCutChangeLeftOrRight);
		this._config.building.leanto.right.cut_back.signal.add(this.checkForAddonsForLeantoCutChangeLeftOrRight);
		this._config.building.leanto.right.cut_front.signal.add(this.checkForAddonsForLeantoCutChangeLeftOrRight);
	}

	public removeListeners()
	{
		this._config.building.width.signal.remove(this.checkForAddonsForWidthChange);
		this._config.building.length.signal.remove(this.checkForAddonsForLengthChange);
		this._config.building.height.signal.remove(this.checkForAddonsForHeightChange);
		this._config.building.single_slope.signal.remove(this.checkForAddonsForSingleSlopeChange);

		this._config.building.single_slope.signal.remove(this.checkForMaximumHeight);
		this._config.building.pitch.signal.remove(this.checkForMaximumHeight);
		this._config.building.height.signal.remove(this.checkForMaximumHeight);
		this._config.building.width.signal.remove(this.checkForMaximumHeight);

		this._config.building.leanto.front.cut_back.signal.remove(this.checkForAddonsForLeantoCutChangeFrontOrBack);
		this._config.building.leanto.front.cut_front.signal.remove(this.checkForAddonsForLeantoCutChangeFrontOrBack);
		this._config.building.leanto.back.cut_back.signal.remove(this.checkForAddonsForLeantoCutChangeFrontOrBack);
		this._config.building.leanto.back.cut_front.signal.remove(this.checkForAddonsForLeantoCutChangeFrontOrBack);

		this._config.building.leanto.left.cut_back.signal.remove(this.checkForAddonsForLeantoCutChangeLeftOrRight);
		this._config.building.leanto.left.cut_front.signal.remove(this.checkForAddonsForLeantoCutChangeLeftOrRight);
		this._config.building.leanto.right.cut_back.signal.remove(this.checkForAddonsForLeantoCutChangeLeftOrRight);
		this._config.building.leanto.right.cut_front.signal.remove(this.checkForAddonsForLeantoCutChangeLeftOrRight);
	}

	private onClick = (event:MouseEvent) =>
	{
		const targetDiv = <HTMLElement>event.target;
		if (targetDiv.getAttribute && targetDiv.getAttribute("data-type") == "nextStep")
		{
			const target_orderId = targetDiv.getAttribute("data-target");
			for (let i = 0, ln = this._closingContainers.length; i < ln; ++i)
			{
				const container = this._closingContainers[i];
				if (container.element.getAttribute("data-orderId") == target_orderId)
				{
					container.open();
				}
				else
				{
					container.close();
				}
			}
		}
	};

	private createBuildingSize()
	{
		const buildingConfig = this._config.config.building;
		this._buildingSize = this.addFieldSet(buildingConfig);

		this.addSingleSlope();

		this._buildingSize.open();
	}

	private addSingleSlope()
	{
		this._singleSlope = <SelectInput<any>>this._buildingSize.fields["single_slope"].input;
	}

	private createOverhangSection()
	{
		const overhangConfig = this._config.config.overhang;
		this._overHang = this.addFieldSet(overhangConfig);

		this.addInfoBubbleToOverhang();
	}

	private addInfoBubbleToOverhang()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._overHang.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			let outerDivStyle = "width: 675px; margin-left: -150px; margin-top: -70px; margin-bottom: -60px; padding: 10px; text-align: left;";
			let imgStyle = "margin-top: 10px;";
			let imgSize = "big1";

			if (this.isInMobileMode)
			{
				outerDivStyle = "width: 400px; max-width: calc(100% + 20px); margin-left: -20px; text-align: left;";
				imgStyle = "max-width: 100%; max-height: 231px;";
				imgSize = "small";
			}

			await CustomPopupWindow.open("",
				`<div class="vbox" style="${outerDivStyle} overflow: auto; background: #515151;">
					<center><b style="font-size: 26pt;">Overhangs</b></center>
					<hr width=100% />
					Eave (Sides) and Gable (Ends) Roof Extensions – 
					These add visual appeal to the building and can extend as much as 5 feet.
					<center>
						<img src="assets/images/overhangs-${imgSize}.jpg"style="${imgStyle}">
					</center>
				</div>`,
				true, "Close", null, true, "OverhangsPopup");
		};
	}

	private createAddons()
	{
		for (const field of this._config.config.colors.fields)
		{
			if (field.id === 'color_rollUp')
			{
				field.values = this._config.config.colors.values;
				this._scenePanel = new ScenePanel(this._scene, this._config.config.leanto.orderId + 1, new Field(field));
				this.addClosingContainer(this._scenePanel);
				this.addInfoBubbleToAddons();
				break;
			}
		}
	}

	private addInfoBubbleToAddons()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._scenePanel.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
				await CustomPopupWindow.open("Doors and Windows",
					`<div class="vbox" style="text-align: left;">
						<p>
						Roll-Up doors are manufactured by Mueller and are available in a variety of colors. If you only need a large opening, use the "Framed-Opening-Only" option.  					
						</p>
					</div>`,
				true, "Close", null, true, "AddOnsPopup");
		};
	}

	// ------------------------------------------------------------------------------------------------------
	// Ridge vent

	private updateColorRidgeVentVisibility = () =>
	{
		if (this._colorRidgeVent)
		{
			if (this._ridgeVentType.value.id === "none")
			{
				this._colorRidgeVent.hide();
			}
			else
			{
				this._colorRidgeVent.show();
			}
		}
	};

	private createRidgeVentSection()
	{
		const config = this._config.config.ridge_vent;

		this._ridgeVent = this.addFieldSet(config);

		this._ridgeVentMode = <BooleanInput>this._ridgeVent.fields["ridge_vent_mode"].input;
		this._ridgeVentType = <SelectInput<string>>this._ridgeVent.fields["ridge_vent_type"].input;

		this._ridgeVentText = document.createElement("div");
		this._ridgeVentText.textContent = "You can't have ridge vents on a single slope building.";
		this._ridgeVentMode.element.parentElement.parentElement.appendChild(this._ridgeVentText);

		this._config.building.single_slope.signal.add(this.updateRidgeVentMode);
		this._config.building.ridge_vent_type.signal.add(this.updateRidgeVentMode);
		this.updateRidgeVentMode();
		this._ridgeVentType.changeSignal.add(this.updateColorRidgeVentVisibility);

		this.addInfoBubbleToRidgeVent();
		
	}
	
	private addInfoBubbleToRidgeVent()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._ridgeVent.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			await CustomPopupWindow.open("Ridge Vents",
				`<div class="vbox" style="text-align: left;">
					<p>
					Heat rises, so this is a great way to help cool and ventilate your building in hot summers. 
					<br/><br/>
					<u>Standard Ridge Vent</u> – This is Mueller's Commercial Ridge vent – it's 10 feet long and has a damper that 
					can be closed for winter months. It has an Airflow FSA = 895 Square Inches and is available in all Mueller Colors.
					<br/><br/>
					<u>Low Ridge Vent</u> – This is Mueller's Low Profile Ridge vent – it's 10 feet long with no damper, has an Airflow FSA = 166 Square Inches,
					and is also available in all Mueller Colors.					
					</p>
				</div>`,
			true, "Close", null, true, "RidgeVentPopup");
		};
	}
	

	// ------------------------------------------------------------------------------------------------------
	// Color

	private createColorSelection()
	{
		const colorsConfig = this._config.config.colors;
		const colorValues = colorsConfig.values;

		// add color values to each field config
		const fieldsConfig = colorsConfig.fields;
		for (let i = 0; i < fieldsConfig.length; i++)
		{
			fieldsConfig[i].values = colorValues;
		}

		this._colors = this.addFieldSet(colorsConfig);

		this._colorWall      = <DropDownInput<string>>this._colors.fields["color_wall"].input;
		this._colorRoof      = <DropDownInput<string>>this._colors.fields["color_roof"].input;
		this._colorTrim      = <DropDownInput<string>>this._colors.fields["color_trim"].input;
		this._colorRollUp    = <DropDownInput<string>>this._colors.fields["color_rollUp"].input;
		this._colorWainscot  = <DropDownInput<string>>this._colors.fields["color_wainscot"].input;
		this._colorRidgeVent = <DropDownInput<string>>this._colors.fields["color_vent"].input;

		this.updateColorRollupVisibility();
		Main.getInstance().scene.building.signals.addonsChange.add(this.updateColorRollupVisibility);

		//this._scenePanel.addRollupColorField();
	}

	// ------------------------------------------------------------------------------------------------------
	// Insulation

	private createInsulationSection()
	{
		const config = this._config.config.insulation;

		this._insulation = this.addFieldSet(config);

		this.addRenderingDisclaimer(this._insulation.element, "*Insulation");

		//<SelectInput<any>>this._insulation.fields["insulation"].input;
		
		this.addInfoBubbleToInsulation();
	}

	private addInfoBubbleToInsulation()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._insulation.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			await CustomPopupWindow.open("Insulation",
				`<div class="vbox" style="text-align: left;">
					<p>
					This is a vinyl reinforced fiberglass insulation used to stabilize the structure's interior temperature and prevent moisture from entering or collecting via condensation.  
					This option will figure a 3" R-10 in the roof and walls. Other thicknesses are available.					
					</p>
				</div>`,
			true, "Close", null, true, "InsulationPopup");
		};
	}

	// ------------------------------------------------------------------------------------------------------
	// TranslucentPanels

	private createTranslucentPanelsSection()
	{
		const config = this._config.config.translucentPanels;

		this._translucentPanels = this.addFieldSet(config);

		this.addRenderingDisclaimer(this._translucentPanels.element, "*Translucent panels");

		<SelectInput<any>>this._translucentPanels.fields["translucent_panels"].input;

		this.addInfoBubbleToTP();
	}
	
	private addInfoBubbleToTP()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._translucentPanels.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			await CustomPopupWindow.open("Translucent Panels",
				`<div class="vbox" style="text-align: left;">
					<p>
					These allow light into the building. They are the same shape as the metal sheet and take the place of the sheet to allow light to transfer into the building. 
					It's best to use them at the top of walls where they are less susceptible to hail damage failure.					
					</p>
				</div>`,
			true, "Close", null, true, "TPPopup");
		};
	}

	// ------------------------------------------------------------------------------------------------------
	// WallLinerPanels

	private createWallLinerPanelsSection()
	{
		const config = this._config.config.wallLinerPanels;
		this._wallLinerPanels = this.addFieldSet(config);

		this.addRenderingDisclaimer(this._wallLinerPanels.element, "*Liner panels");

		//<SelectInput<any>>this._wallLinerPanels.fields["wall_liner_panels"].input;

		this.addInfoBubbleToLP();
	}

	private addInfoBubbleToLP()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._wallLinerPanels.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			await CustomPopupWindow.open("Wall Liner Panels",
				`<div class="vbox" style="text-align: left;">
					<p>
					Liner panels serve to protect insulation from damage. Panels are 8'. 
					</p>
				</div>`,
			true, "Close", null, true, "LPPopup");
		};
	}

	private addRenderingDisclaimer(element: HTMLElement, title: string)
	{
		const div = document.createElement("div");
		div.textContent = `${title} not shown on 3d rendering`;
		div.style.color = "#E58032";
		div.style.fontSize = "9pt";
		div.style.fontWeight = "700";
		div.classList.add("disclaimer");

		element.querySelector(".fields")?.appendChild(div);
	}

	public createGetAQuoteSection()
	{
		const config = this._config.config.getAQuote;

		const getaquote = this.addFieldSet(config);

		getaquote.disable();
		getaquote.element.onclick = Main.getInstance().authView.onGetQuoteClick;
	}

	private edgeWorkaround() // this is a workaround for solving the bug when the edge appears abnormally
	{
		if (this._config.building.single_slope.value)
		{
			this._config.building.pitch.signal.remove(this.checkForAddonsForSingleSlopeChange);
			this._config.building.pitch.setValue(this._config.building.pitch.value - 0.01);
			this._config.building.pitch.setValue(this._config.building.pitch.value + 0.01);
			this._config.building.pitch.signal.add(this.checkForAddonsForSingleSlopeChange);
		}
	}

	private checkForAddonsForLeantoCutChangeFrontOrBack = () =>
	{
		this.checkForAddonsForLeanToCutChange("width");
	};

	private checkForAddonsForLeantoCutChangeLeftOrRight = () =>
	{
		this.checkForAddonsForLeanToCutChange("length");
	};

	private checkForAddonsForLeanToCutChange(type: "width" | "length")
	{
		return this.checkForAddons(
			type === "width" ? [Building.WALLS.FRONT, Building.WALLS.BACK] : [Building.WALLS.LEFT, Building.WALLS.RIGHT],
			type === "width" ? [CheckAddonType.WIDTH] : [CheckAddonType.LENGTH],
			true
		);
	}

	private checkForAddonsForWidthChange = async () =>
	{
		if (this._config.isProjectSettingsBeingSet)
		{
			return;
		}
		if (await this.checkForAddons(Array(Building.WALLS.FRONT, Building.WALLS.BACK, Building.WALLS.RIGHT), [CheckAddonType.WIDTH]))
		{
			// the user pressed "OK" on the confirmation, the addons in question are gone
		}
		else
		{
			this._config.building.width.signal.remove(this.checkForAddonsForWidthChange);
			this._config.building.width.setValue(this._lastWidthValue);
			(<SelectInput<any>>this._buildingSize.fields["width"].input).value = this._lastWidthValue;
			this._config.building.width.signal.add(this.checkForAddonsForWidthChange);
		}

		this._lastWidthValue = this._config.building.width.value;
		this.edgeWorkaround();
	};

	private checkForAddonsForLengthChange = async () =>
	{
		if (this._config.isProjectSettingsBeingSet)
		{
			return;
		}
		if (await this.checkForAddons(Array(Building.WALLS.RIGHT, Building.WALLS.LEFT), [CheckAddonType.LENGTH]))
		{
			// the user pressed "OK" on the confirmation, the addons in question are gone
		}
		else // the user canceled the confirmation, we need to change this setting back
		{
			this._config.building.length.signal.remove(this.checkForAddonsForLengthChange);
			this._config.building.length.setValue(this._lastLengthValue);
			(<SelectInput<any>>this._buildingSize.fields["length"].input).value = this._lastLengthValue;
			this._config.building.length.signal.add(this.checkForAddonsForLengthChange);
		}

		if (this._lastLengthValue > this._config.building.length.value)
		{
			// if we decrease the size, we should workaround the bug like this
			this._config.building.length.signal.remove(this.checkForAddonsForLengthChange);
			this._config.building.length.setValue(this._config.building.length.value - 1);
			this._config.building.length.setValue(this._config.building.length.value + 1);
			this._config.building.length.signal.add(this.checkForAddonsForLengthChange);
		}
		else if (this._lastLengthValue < this._config.building.length.value)
		{
			// if we increase the size, we should workaround the bug like this
			this._config.building.length.signal.remove(this.checkForAddonsForLengthChange);
			this._config.building.length.setValue(this._config.building.length.value + 1);
			this._config.building.length.setValue(this._config.building.length.value - 1);
			this._config.building.length.signal.add(this.checkForAddonsForLengthChange);
		}

		this._lastLengthValue = this._config.building.length.value;
		this.edgeWorkaround();
	};

	public checkForMaximumHeight = async () =>
	{
		if (this._config.isProjectSettingsBeingSet)
		{
			return;
		}
		const height = this._config.building.height.value;
		const widthFactor = this._config.building.single_slope.value ? this._config.building.width.value : this._config.building.width.value / 2;
		const plusHeight = (this._config.building.pitch.value / 12) * widthFactor;
		const maximumHeight = height + plusHeight;

		// as requested by Jason
		const limitToSingleSlopeOnly = true;

		if ((!limitToSingleSlopeOnly || (limitToSingleSlopeOnly && this._config.building.single_slope.value)) && maximumHeight > 30)
		{
			await WarningWindow.open(`You have exceeded the 30' maximum wall height. Please either reduce roof pitch and/or wall height.`);

			await this.checkForAddons(Array(Building.WALLS.RIGHT, Building.WALLS.FRONT, Building.WALLS.BACK), [CheckAddonType.HEIGHT], true);
			
			this._config.building.single_slope.setValue(false);
			(<BooleanInput>this._buildingSize.fields["single_slope"].input).setChecked(false);

			if (!limitToSingleSlopeOnly)
			{
				this._config.building.pitch.setValue(1);
				(this._buildingSize.fields["pitch"].input as NumberInput).value = 1;
			}
		}
	};

	public undoBuildingHeightChange(height){
		this._config.building.height.setValue(height);
		(this._buildingSize.fields["height"].input as NumberInput).value = height;
	}

	public checkForAddonsForHeightChange = async () =>
	{
		if (this._config.isProjectSettingsBeingSet)
		{
			return;
		}
		if (this._config.building.height.value < this._lastHeightValue)
		{
			if (await this.checkForAddons(Array(Building.WALLS.RIGHT, Building.WALLS.LEFT, Building.WALLS.FRONT, Building.WALLS.BACK), [CheckAddonType.HEIGHT]))
			{
				// the user pressed "OK" on the confirmation, the addons in question are gone
			}
			else // the user canceled the confirmation, we need to change this setting back
			{
				this._config.building.height.signal.remove(this.checkForAddonsForHeightChange);
				this._config.building.height.setValue(this._lastHeightValue);
				(<SelectInput<any>>this._buildingSize.fields["height"].input).value = this._lastHeightValue;
				this._config.building.height.signal.add(this.checkForAddonsForHeightChange);
			}
		}

		this._lastHeightValue = this._config.building.height.value;
		this.edgeWorkaround();
	};

	public checkForAddonsForSingleSlopeChange = async () => // we need to remove some addons when the user turns OFF single_slope, because they might get into invalid positions
	{
		if (this._config.isProjectSettingsBeingSet)
		{
			return;
		}
		if (!this._config.building.single_slope.value)
		{
			if (await this.checkForAddons([Building.WALLS.RIGHT, Building.WALLS.FRONT, Building.WALLS.BACK], [CheckAddonType.HEIGHT]))
			{
				// the user pressed "OK" on the confirmation
			}
			else // the user canceled the confirmation, we need to change this setting back
			{
				this._config.building.single_slope.signal.remove(this.checkForAddonsForSingleSlopeChange);
				this._config.building.single_slope.setValue(true);
				(<BooleanInput>this._buildingSize.fields["single_slope"].input).setChecked(true);
				this._config.building.single_slope.signal.add(this.checkForAddonsForSingleSlopeChange);
			}
			this._config.building.width.signal.remove(this.checkForAddonsForWidthChange);
			this._config.building.width.setValue(this._config.building.width.value + 1);
			this._config.building.width.setValue(this._config.building.width.value - 1);
			this._config.building.width.signal.add(this.checkForAddonsForWidthChange);
		}

		this.edgeWorkaround();
	};

	private whichBay(addon: ISceneAddon, postPositions: Array<number>): number
	{
		let ret = -1;
		for (let i = 0; i < postPositions.length - 1; ++i)
		{
			if (addon.getPosition().horizontal > postPositions[i] && addon.getPosition().horizontal < postPositions[i+1])
			{
				ret = i;
				break;
			}
		}

		return ret;
	}

	private howManyAddonsInBay(addons: Array<ISceneAddon>, postPositions: Array<number>, whichBayTheAddonIsPresent: number): number
	{
		let count = 0;
		for (let i = 0; i < addons.length; ++i)
		{
			const addon = addons[i];
			if (addon.getPosition().horizontal > postPositions[whichBayTheAddonIsPresent] && addon.getPosition().horizontal < postPositions[whichBayTheAddonIsPresent + 1])
			{
				count++;
			}
		}

		return count;
	}

	private getInvalidAddonsForType(wallIds: WallSide[], type: CheckAddonType, forceDelete: boolean = false)
	{
		const addons = Main.getInstance().scene.building.addons;
		const invalidAddons: ISceneAddon[] = [];

		for (let i = 0; i < wallIds.length; ++i)
		{
			const wallId = wallIds[i];
			let checkForTopsToo = false;
			if (type === CheckAddonType.WIDTH)
			{
				if (wallId === Building.WALLS.RIGHT && this._config.building.single_slope.value)
				{
					const isWidthDecreasing = this._lastWidthValue > this._config.building.width.value;
					if (isWidthDecreasing === true)
					{
						checkForTopsToo = true; // we need to check the right wall's tops too, if the building is single sloped, and width is being changed
					}
					else
					{
						continue;
					}
				}
				else if (wallId === Building.WALLS.FRONT || wallId === Building.WALLS.BACK)
				{
					checkForTopsToo = true;
				}
			}

			for (let j = addons[wallId].length - 1; j >= 0; --j)
			{
				const addon = addons[wallId][j];
				let condition = false;

				const widthOrLength: "width" | "length" = addon.parentId === "left" || addon.parentId === "right" ? "length" : "width";
				const buildingSize = this._config.building[widthOrLength].value;
				const lastBuildingSize = widthOrLength === "width" ? this._lastWidthValue : this._lastLengthValue;

				switch (type)
				{
					case CheckAddonType.HEIGHT: // we check addons to the top, because we change the height of the building. Or we change the width (front and back) or singleslope
						const addonHeight = WallAddon.getTopOfAddonWithOffset(addon);

						const buildingHeight = this._config.building.single_slope.value ?  Main.getInstance().config.building.height.value + Main.getInstance().config.building.pitch.value  :  Main.getInstance().config.building.height.value;

						condition = addonHeight > buildingHeight || Main.getInstance().scene.building.leanToManager.isThereAConflictWithAddons(Array(Building.WALLS.RIGHT, Building.WALLS.LEFT, Building.WALLS.FRONT, Building.WALLS.BACK));
						break;
					case CheckAddonType.LENGTH: // we check addons on the left and right walls, because we change the length of the building
						if (widthOrLength === "length")
						{
							if (addon instanceof WallAddon)
							{
								const clampedZ = addon.pushAwayFromColumns(addon.getPosition().horizontal, buildingSize);
								if (clampedZ !== addon.getPosition().horizontal)
								{
									condition = true;
								}
							}
							const numberOfBays = Math.ceil(buildingSize / PostManager.IntervalOnSide);
							const requiredPosts = numberOfBays + 1;


							//
							// check if there's more than one addon in the same bay. More could cause errors, so we better remove them.
							//

							const postPositions = [];
							postPositions.push(-buildingSize / 2);
							for (let k = 1; k < requiredPosts - 1; ++k)
							{
								postPositions.push(-buildingSize / 2 + k / (requiredPosts - 1) * buildingSize);
							}
							postPositions.push(buildingSize / 2);

							const whichBayTheAddonIsPresent = this.whichBay(addon, postPositions);
							const isAnyOtherAddonInTheSameBay = this.howManyAddonsInBay(addons[wallId], postPositions, whichBayTheAddonIsPresent) > 1;

							if (!addon.hasValidPlace || (lastBuildingSize !== buildingSize && isAnyOtherAddonInTheSameBay))
							{
								condition = true;
								forceDelete = true; // we have to delete them, because we cannot undo the changesize without a bug
							}

							///


							const sizeOfABay = buildingSize / numberOfBays;
							let offset = WallAddon.isAddonRollup(addon) ? 2 : 0.35; // rollups have to be 2 feet away from columns (+columnwidth/2)
							if (numberOfBays === 1 && offset < 0.5) // when the building is so small, we have to leave out more space for windows, too
							{
								offset += 2;
							}
							if (sizeOfABay < addon.width + 2 * offset)
							{
								condition = true;
							}
							if (Math.abs(addon.getPosition().horizontal) + addon.width / 2 + 2 > buildingSize / 2)
							{
								condition = true;
							}
							if (Main.getInstance().scene.building.leanToManager.isThereAConflictWithAddons(Array(Building.WALLS.RIGHT, Building.WALLS.LEFT, Building.WALLS.FRONT, Building.WALLS.BACK)))
							{
								condition = true;
							}
							break;
						}
					case CheckAddonType.WIDTH: // we check addons on the front and back, because we change the width of the building
						if (widthOrLength === "width")
						{
							if (addon instanceof WallAddon)
							{
								const clampedZ = addon.pushAwayFromColumns(addon.getPosition().horizontal, buildingSize);
								if (clampedZ !== addon.getPosition().horizontal)
								{
									condition = true;
								}
							}
							if (Math.abs(addon.getPosition().horizontal) + addon.width / 2 + 2 > buildingSize / 2 && (wallId === Building.WALLS.FRONT || wallId === Building.WALLS.BACK))
							{
								condition = true;
							}

							if (Main.getInstance().scene.building.leanToManager.isThereAConflictWithAddons(Array(Building.WALLS.RIGHT, Building.WALLS.LEFT, Building.WALLS.FRONT, Building.WALLS.BACK)))
							{
								condition = true;
							}

							if (checkForTopsToo === true) // TODO duplication, would be nice to find another solution
							{
								const addonHeight = WallAddon.getTopOfAddonWithOffset(addon);
								condition = condition || addonHeight > Main.getInstance().config.building.height.value;
							}
							break;
						}
				}
				if (condition)
				{
					invalidAddons.push(addon);
				}
			}
		}

		return {
			invalidAddons,
			forceDelete
		};
	}

	private getInvalidAddons(wallIds: WallSide[], types: CheckAddonType[], forceDelete: boolean = false): {invalidAddons: ISceneAddon[], forceDelete: boolean}
	{
		const invalidAddons: ISceneAddon[] = [];

		for (const type of types)
		{
			const invalidAddsForType = this.getInvalidAddonsForType(wallIds, type, forceDelete);
			invalidAddons.push(...invalidAddsForType.invalidAddons);
			forceDelete = forceDelete || invalidAddsForType.forceDelete;
		}

		return {
			invalidAddons,
			forceDelete
		};
	}

	public checkForAddons = async (wallIds: WallSide[], types: CheckAddonType[], forceDelete: boolean = false): Promise<boolean> =>
	{
		const invalidAdds = this.getInvalidAddons(wallIds, types, forceDelete);
		const addonsToDelete = invalidAdds.invalidAddons;
		forceDelete = invalidAdds.forceDelete;

		if (addonsToDelete.length > 0)
		{
			const alertText = "The building violates some building rules, some doors or windows may be removed because of structural requirements. You may need to review and add those components back in after this change has been made.";
			if (forceDelete)
			{
				await WarningWindow.open(alertText);
				for (let i = addonsToDelete.length-1; i >= 0; --i)
				{
					Main.getInstance().scene.building.scene.destroyAddon(addonsToDelete[i]);
				}
				addonsToDelete.length = 0;

				return true;
			}
			if (await ConfirmWindow.open(`${alertText} Continue?`))
			{
				for (let i = addonsToDelete.length-1; i >= 0; --i)
				{
					Main.getInstance().scene.building.scene.destroyAddon(addonsToDelete[i]);
				}
				addonsToDelete.length = 0;

				return true;
			}
			else
			{
				return false;
			}
		}

		return true;
	};

	private isThereARollupOnTheBuilding()
	{
		const sides = [Building.WALLS.FRONT, Building.WALLS.BACK, Building.WALLS.LEFT, Building.WALLS.RIGHT];

		for (const side of sides)
		{
			for (const addon of Main.getInstance().scene.building.addons[side])
			{
				if (WallAddon.isAddonRollup(addon, false))
				{
					return true;
				}
			}
		}

		return false;
	};

	private updateColorRollupVisibility = () =>
	{
		if (this.isThereARollupOnTheBuilding())
		{
			this._colorRollUp.show();
		}
		else
		{
			this._colorRollUp.hide();
		}
	};

	private updateRidgeVentMode = () =>
	{
		const single_slope = this._config.building.single_slope.value;
		const hasSingleSlope = single_slope && single_slope;

		// if single slope is turned on -> type should be disabled
		this._ridgeVentType.setEnabled(!hasSingleSlope);

		if (hasSingleSlope)
		{
			// single slope is turned on -> we automatically set ridge_vent_type to none
			// checkChange is used to avoid infinite recursion
			this._config.building.ridge_vent_type.setValue(RidgeVent.TYPE_NONE, true);
			this._ridgeVentType.setValueByKey(RidgeVent.TYPE_NONE);
			this._ridgeVentText.classList.remove("hidden");
		}
		else
		{
			this._ridgeVentText.classList.add("hidden");
		}

		const type = this._config.building.ridge_vent_type.value;
		const hasType = type !== RidgeVent.TYPE_NONE;

		this._ridgeVentMode.setEnabled(!hasSingleSlope && hasType);

		this.updateColorRidgeVentVisibility();
	};

	// ------------------------------------------------------------------------------------------------------
	// Wainscot

	private updateColorWainscotVisibility = () =>
	{
		if (this._wainscotInput.value)
		{
			this._colorWainscot.show();
		}
		else
		{
			this._colorWainscot.hide();
		}
	};

	private createWainscotSection()
	{
		const config = this._config.config.wainscot;

		this._wainscot = this.addFieldSet(config);

		this.addInfoBubbleToWainscot();

		this._wainscotInput = <BooleanInput>this._wainscot.fields["wainscot"].input;
		this._wainscotInput.changeSignal.add(this.updateColorWainscotVisibility);

	}

	private addInfoBubbleToWainscot()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._wainscot.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			let outerDivStyle = "width: 675px; margin-left: -150px; margin-top: -70px; margin-bottom: -60px; padding: 10px; text-align: left;";
			let imgStyle = "margin-top: 10px;";
			let imgSize = "big1";

			if (this.isInMobileMode)
			{
				outerDivStyle = "width: 400px; max-width: calc(100% + 20px); margin-left: -20px; text-align: left;";
				imgStyle = "max-width: 100%; max-height: 231px;";
				imgSize = "small";
			}
			await CustomPopupWindow.open("",
				`<div class="vbox" style="${outerDivStyle} overflow: auto; background: #515151;">
					<center><b style="font-size: 26pt;">Wainscot</b></center>
					<hr width=100% />
					This will add curb appeal to your building.  You can choose a different color for the lower 4-foot section of the building.
					<center>
						<img src="assets/images/wainscot-${imgSize}.jpg"style="${imgStyle}">
					</center>
				</div>`,
				true, "Close", null, true, "WainscotPopup");				
		};
	}

	private createInsetBaySection()
	{
		this._insetBay = new InsetBayFieldSet(this._config.building.insetBay, this._config.building.insetBay.orderId);
		this.addClosingContainer(this._insetBay);

		this.disableFrontFields();
		this.disableBackFields();

		(<HTMLInputElement>(<any>this._insetBay.fields).front.input.element).addEventListener("change", this.frontBayCountChanged);
		(<HTMLInputElement>(<any>this._insetBay.fields).back.input.element).addEventListener( "change", this.backBayCountChanged);

		//(<HTMLInputElement>(<any>this._insetBay.fields).front_drop.input.element).addEventListener( "change",    this.frontBayDropChanges);
		(<HTMLInputElement>(<any>this._insetBay.fields).front_length_1.input.element).addEventListener("input",  this.frontBayLength1Changes);
		(<HTMLInputElement>(<any>this._insetBay.fields).front_length_1.input.element).addEventListener("change", this.frontBayLength1Changes);
		(<HTMLInputElement>(<any>this._insetBay.fields).front_length_2.input.element).addEventListener("input",  this.frontBayLength2Changes);
		(<HTMLInputElement>(<any>this._insetBay.fields).front_length_2.input.element).addEventListener("change", this.frontBayLength2Changes);
		//(<HTMLInputElement>(<any>this._insetBay.fields).front_overhang.input.element).addEventListener("input",  this.frontBayOverhangChanges);
		//(<HTMLInputElement>(<any>this._insetBay.fields).front_overhang.input.element).addEventListener("change", this.frontBayOverhangChanges);

		//(<HTMLInputElement>(<any>this._insetBay.fields).back_drop.input.element).addEventListener( "change",     this.backBayDropChanges);
		(<HTMLInputElement>(<any>this._insetBay.fields).back_length_1.input.element).addEventListener("input",   this.backBayLength1Changes);
		(<HTMLInputElement>(<any>this._insetBay.fields).back_length_1.input.element).addEventListener("change",  this.backBayLength1Changes);
		(<HTMLInputElement>(<any>this._insetBay.fields).back_length_2.input.element).addEventListener("input",   this.backBayLength2Changes);
		(<HTMLInputElement>(<any>this._insetBay.fields).back_length_2.input.element).addEventListener("change",  this.backBayLength2Changes);
		//(<HTMLInputElement>(<any>this._insetBay.fields).back_overhang.input.element).addEventListener("input",   this.backBayOverhangChanges);
		//(<HTMLInputElement>(<any>this._insetBay.fields).back_overhang.input.element).addEventListener("change",  this.backBayOverhangChanges);
	}
	private frontBayCountChanged = () =>
	{
		Main.getInstance().config.building.insetBay.front.count.setValue((<HTMLInputElement>(<any>this._insetBay.fields).front.input).value);
		this.checkDisabledFieldsForFront();
	}
	private backBayCountChanged = () =>
	{
		Main.getInstance().config.building.insetBay.back.count.setValue((<HTMLInputElement>(<any>this._insetBay.fields).back.input).value);
		this.checkDisabledFieldsForBack();
	}
	private disableFrontFields = () =>
	{
		//(<HTMLInputElement>(<any>this._insetBay.fields).front_drop.input.element).classList.add("disabled");
		(<HTMLInputElement>(<any>this._insetBay.fields).front_length_1.input.element).classList.add("disabled");
		(<HTMLInputElement>(<any>this._insetBay.fields).front_length_2.input.element).classList.add("disabled");
		//(<HTMLInputElement>(<any>this._insetBay.fields).front_overhang.input.element).classList.add("disabled");
	}
	private disableBackFields = () =>
	{
		//(<HTMLInputElement>(<any>this._insetBay.fields).back_drop.input.element).classList.add("disabled");
		(<HTMLInputElement>(<any>this._insetBay.fields).back_length_1.input.element).classList.add("disabled");
		(<HTMLInputElement>(<any>this._insetBay.fields).back_length_2.input.element).classList.add("disabled");
		//(<HTMLInputElement>(<any>this._insetBay.fields).back_overhang.input.element).classList.add("disabled");
	}
	private checkDisabledFieldsForFront = () =>
	{
		this.disableFrontFields();

		//const drop = <HTMLInputElement>(<any>this._insetBay.fields).front_drop.input.element;
		//const overhang = <HTMLInputElement>(<any>this._insetBay.fields).front_overhang.input.element;

		const lengths = [];
		lengths.push((<HTMLInputElement>(<any>this._insetBay.fields).front_length_1.input.element));
		lengths.push((<HTMLInputElement>(<any>this._insetBay.fields).front_length_2.input.element));

		for (let i = 0; i < (<any>this._insetBay).fields.front.input.value; ++i)
		{
			/*if (i == 0)
			{
				//drop.classList.remove("disabled");
				overhang.classList.remove("disabled");
			}*/
			(<HTMLInputElement>lengths[i]).classList.remove("disabled");
		}

	}
	/*private frontBayDropChanges = () =>
	{
		Main.getInstance().config.building.insetBay.front.drop.setValue((<HTMLInputElement>(<any>this._insetBay.fields).front_drop.input).value);
	}*/
	private frontBayLength1Changes = () =>
	{
		const lengthInConfig = Main.getInstance().config.building.insetBay.front.length_1;
		const lengthInSettings = (<HTMLInputElement>(<any>this._insetBay.fields).front_length_1.input);
		if (lengthInConfig.value !== lengthInSettings.value)
		{
			lengthInConfig.setValue(lengthInSettings.value);
		}
	}
	private frontBayLength2Changes = () =>
	{
		const lengthInConfig = Main.getInstance().config.building.insetBay.front.length_2;
		const lengthInSettings = (<HTMLInputElement>(<any>this._insetBay.fields).front_length_2.input);
		if (lengthInConfig.value !== lengthInSettings.value)
		{
			lengthInConfig.setValue(lengthInSettings.value);
		}
	}
	/*private frontBayOverhangChanges = () =>
	{
		const overhangInConfig = Main.getInstance().config.building.insetBay.front.overhang;
		const overhangInSettings = (<HTMLInputElement>(<any>this._insetBay.fields).front_overhang.input);

		if (overhangInConfig.value !== overhangInSettings.value)
		{
			overhangInConfig.setValue(overhangInSettings.value);
		}
	}*/
	/*private backBayDropChanges = () =>
	{
		Main.getInstance().config.building.insetBay.back.drop.setValue((<HTMLInputElement>(<any>this._insetBay.fields).back_drop.input).value);
	}*/
	private backBayLength1Changes = () =>
	{
		const lengthInConfig = Main.getInstance().config.building.insetBay.back.length_1;
		const lengthInSettings = (<HTMLInputElement>(<any>this._insetBay.fields).back_length_1.input);
		if (lengthInConfig.value !== lengthInSettings.value)
		{
			lengthInConfig.setValue(lengthInSettings.value);
		}
	}
	private backBayLength2Changes = () =>
	{
		const lengthInConfig = Main.getInstance().config.building.insetBay.back.length_2;
		const lengthInSettings = (<HTMLInputElement>(<any>this._insetBay.fields).back_length_2.input);
		if (lengthInConfig.value !== lengthInSettings.value)
		{
			lengthInConfig.setValue(lengthInSettings.value);
		}
	}
	/*private backBayOverhangChanges = () =>
	{
		const overhangInConfig = Main.getInstance().config.building.insetBay.back.overhang;
		const overhangInSettings = (<HTMLInputElement>(<any>this._insetBay.fields).back_overhang.input);

		if (overhangInConfig.value !== overhangInSettings.value)
		{
			overhangInConfig.setValue(overhangInSettings.value);
		}
	}*/
	private checkDisabledFieldsForBack = () =>
	{
		this.disableBackFields();
		//const drop     = <HTMLInputElement>(<any>this._insetBay.fields).back_drop.input.element;
		//const overhang = <HTMLInputElement>(<any>this._insetBay.fields).back_overhang.input.element;

		const lengths = [];
		lengths.push((<HTMLInputElement>(<any>this._insetBay.fields).back_length_1.input.element));
		lengths.push((<HTMLInputElement>(<any>this._insetBay.fields).back_length_2.input.element));

		for (let i = 0; i < (<any>this._insetBay.fields).back.input.value; ++i) {
			/*if (i === 0)
			{
				//drop.classList.remove("disabled");
				overhang.classList.remove("disabled");
			}*/
			(<HTMLInputElement>lengths[i]).classList.remove("disabled");
		}
	}

	private updateLeantoWallsToggler = () =>
	{
		const isThereALeanToEnabled = this._leanTo.leanToSettings.some((leanToSetting: LeanToSetting) => leanToSetting.isEnabled);
		if (isThereALeanToEnabled)
		{
			this._leanTo.wallField.enable();
		}
		else
		{
			this._config.building.leanto.wall.setValue(false);
			this._leanTo.wallField.disable();
		}
	};

	private createLeanToSection()
	{
		const leanToSection = new LeanToFieldSet(this._config.building.leanto, this._config.config.leanto.orderId);
		this._leanTo = leanToSection;
		for (const leanToSetting of this._leanTo.leanToSettings)
		{
			leanToSetting.leanToSettingConfig.enabled.signal.add(this.updateLeantoWallsToggler);
		}
		this.addClosingContainer(leanToSection);
	}

	private createGutterSection()
	{
		const config = this._config.config.gutter;
		this._gutter = this.addFieldSet(config);

		this._gutterInput = <BooleanInput>this._gutter.fields["gutter"].input;
		
		this.addInfoBubbleToGutter();

	}

	private addInfoBubbleToGutter()
	{
		const div = document.createElement("div");
		div.classList.add("infoBubbleButton");
		div.textContent = "?";
		const parentElement = this._gutter.element.querySelector(".fields");
		parentElement.appendChild(div);

		div.onclick = async () =>
		{
			await CustomPopupWindow.open("Gutter",
				`<div class="vbox" style="text-align: left;">
					<p>
					Gutters help control water coming off the building.
					</p>
				</div>`,
			true, "Close", null, true, "GutterPopup");
		};
	}

	private createSupportBeamSection()
	{
		const config = this._config.config.supportBeam;
		this._supportBeam = this.addFieldSet(config);

		this._supportBeamInput = <BooleanInput>this._supportBeam.fields["supportBeam"].input;
	}

	private addFieldSet(config:any)
	{
		const fieldSet = new FieldSet(config, this.onFieldChange);
		this.addClosingContainer(fieldSet);

		return fieldSet;
	}

	private addClosingContainer(closingContainer:ClosingContainer)
	{
		this._closingContainers.push(closingContainer);
		this._fieldsContainer.appendChild(closingContainer.element);

		closingContainer.openSignal.add(this.onClosingContainerOpen);
	}

	private onClosingContainerOpen = (open:boolean, target:ClosingContainer):void =>
	{
		// close all other ClosingContainers when the user opens one

		for (let i = 0, ln = this._closingContainers.length; i < ln; ++i)
		{
			const container = this._closingContainers[i];
			if (container !== target)
			{
				container.close();
			}
		}
	};

	public onFieldChange = (value:any, input:IInput) =>
	{
		const buildingConfig = this._config.building;
		const colorsConfig = this._config.colors;

		const id = input.id;

		// todo this is obsolete now with config
		// also its type unsafe
		switch (id)
		{
			case "width":
				buildingConfig.width.setValue(value);
				break;
			case "length":
				buildingConfig.length.setValue(value);
				break;
			case "height":
				buildingConfig.height.setValue(value);
				break;
			case "pitch":
				buildingConfig.pitch.setValue(value);
				break;

			case "overhang_sides":
				buildingConfig.overhang_sides.setValue(value);
				break;
			case "overhang_end":
				buildingConfig.overhang_end.setValue(value);
				break;

			// case "overhang_front":
			// 	buildingConfig.overhang_front.setValue(value);
			// 	break;
			// case "overhang_back":
			// 	buildingConfig.overhang_back.setValue(value);
			// 	break;
			// case "overhang_left":
			// 	buildingConfig.overhang_left.setValue(value);
			// 	break;
			// case "overhang_right":
			// 	buildingConfig.overhang_right.setValue(value);
			// 	break;

			case "no_walls":
				buildingConfig.no_walls.setValue(value);
				break;

			case "single_slope":
				buildingConfig.single_slope.setValue(value);
				break;

			case "color_wall":
				colorsConfig.wall.setValue(value);
				break;
			case "color_roof":
				colorsConfig.roof.setValue(value);
				break;
			case "color_trim":
				colorsConfig.trim.setValue(value);
				break;
			case "color_rollUp":
				colorsConfig.rollUp.setValue(value);
				break;
			case "color_vent":
				colorsConfig.vent.setValue(value);
				break;
			case "color_wainscot":
				colorsConfig.wainscot.setValue(value);
				break;

			case "ridge_vent_type":
				buildingConfig.ridge_vent_type.setValue(value);
				break;
			case "ridge_vent_mode":
				buildingConfig.ridge_vent_mode.setValue(value);
				break;

			case "wainscot":
				buildingConfig.wainscot.setValue(value ? 4 : 0);
				break;

			case "gutter":
				buildingConfig.gutter.setValue(value);
				break;

			case "supportBeam":
				buildingConfig.supportBeam.setValue(value);
				break;

			case "insulation":
				buildingConfig.insulation.setValue(value);
				break;

			case "translucent_panels":
				buildingConfig.translucentPanels.setValue(value);
				break;

			case "wall_liner_panels":
				if (value === "#ffffff")
				{
					value = "none";
				}
				buildingConfig.wallLinerPanels.setValue(value);
				break;
		}
	};

	/**
	 * Called when a project is loaded. Sets the field values.
	 * TODO not very nice, fields should listen to config directly
	 */
	public update()
	{
		const buildingConfig = this._config.building;

		this._buildingSize.fields["width"].input.value      = buildingConfig.width.value;
		this._buildingSize.fields["length"].input.value     = buildingConfig.length.value;
		this._buildingSize.fields["height"].input.value     = buildingConfig.height.value;
		this._buildingSize.fields["pitch"].input.value      = buildingConfig.pitch.value;
		this._overHang.fields["overhang_sides"].input.value = buildingConfig.overhang_sides.value;
		this._overHang.fields["overhang_end"].input.value   = buildingConfig.overhang_end.value;
		// this._overHang.fields["overhang_front"].input.value = buildingConfig.overhang_front.value;
		// this._overHang.fields["overhang_back"].input.value  = buildingConfig.overhang_back.value;
		// this._overHang.fields["overhang_left"].input.value  = buildingConfig.overhang_left.value;
		// this._overHang.fields["overhang_right"].input.value = buildingConfig.overhang_right.value;

		// (<HTMLInputElement>(<any>this._insetBay.fields).front.input).value = buildingConfig.insetBay.front.count.value; this.frontBayCountChanged();
		// //(<HTMLInputElement>(<any>this._insetBay.fields).front_drop.input).value = buildingConfig.insetBay.front.drop.value; this.frontBayDropChanges();
		// (<HTMLInputElement>(<any>this._insetBay.fields).front_length_1.input).value = buildingConfig.insetBay.front.length_1.value; this.frontBayLength1Changes();
		// (<HTMLInputElement>(<any>this._insetBay.fields).front_length_2.input).value = buildingConfig.insetBay.front.length_2.value; this.frontBayLength2Changes();
		// //(<HTMLInputElement>(<any>this._insetBay.fields).front_overhang.input).value = buildingConfig.insetBay.front.overhang.value; this.frontBayOverhangChanges();

		// (<HTMLInputElement>(<any>this._insetBay.fields).back.input).value  = buildingConfig.insetBay.back.count.value;  this.backBayCountChanged();
		// //(<HTMLInputElement>(<any>this._insetBay.fields).back_drop.input).value = buildingConfig.insetBay.back.drop.value; this.backBayDropChanges();
		// (<HTMLInputElement>(<any>this._insetBay.fields).back_length_1.input).value = buildingConfig.insetBay.back.length_1.value; this.backBayLength1Changes();
		// (<HTMLInputElement>(<any>this._insetBay.fields).back_length_2.input).value = buildingConfig.insetBay.back.length_2.value; this.backBayLength2Changes();
		// //(<HTMLInputElement>(<any>this._insetBay.fields).back_overhang.input).value = buildingConfig.insetBay.back.overhang.value; this.backBayOverhangChanges();


		(<BooleanInput>this._buildingSize.fields["no_walls"].input).setChecked(<any>buildingConfig.no_walls.value);
		(<BooleanInput>this._buildingSize.fields["single_slope"].input).setChecked(buildingConfig.single_slope.value);

		(<BooleanInput>this._insulation.fields["insulation"].input).setChecked(buildingConfig.insulation.value !== "no");
		(<SelectInput<any>>this._translucentPanels.fields["translucent_panels"].input).value = buildingConfig.translucentPanels.value;
		(<SelectInput<any>>this._wallLinerPanels.fields["wall_liner_panels"].input).setValueByKey((<any>buildingConfig.wallLinerPanels).value);

		this._ridgeVentMode.setChecked(buildingConfig.ridge_vent_mode.value);
		this._ridgeVentType.setValueByKey(buildingConfig.ridge_vent_type.value);

		this._wainscotInput.setChecked(buildingConfig.wainscot.value !== 0);
		this._gutterInput.setChecked(buildingConfig.gutter.value);

		const colorsConfig = this._config.colors;

		this._colorWall.setValueByKey(colorsConfig.wall.value);
		this._colorRoof.setValueByKey(colorsConfig.roof.value);
		this._colorTrim.setValueByKey(colorsConfig.trim.value);
		this._colorRollUp.setValueByKey(colorsConfig.rollUp.value);
		this._colorRidgeVent.setValueByKey(colorsConfig.vent.value);
		this._colorWainscot.setValueByKey(colorsConfig.wainscot.value);

		this.updateColorRidgeVentVisibility();
		this.updateColorWainscotVisibility();
		this.updateLeantoWallsToggler();

		// leanTo listens to the config events in itself
	}

	private initScrollFix()
	{
		this.updateAddonListPosition();
		this._properties.addEventListener("scroll", this.onPropertiesScroll);
	}

	private onPropertiesScroll = () =>
	{
		this.updateAddonListPosition();
	}

	private updateAddonListPosition()
	{
		// onscroll hack:
		// - .addon-list needs to be position absolute in order to show over the scrollbar of #properties
		// - #properties can't be relative because then it will cover parts of .addon-list
		// - so we manually set the margin-top for .addon-list (otherwise absolute positioning results in wrong positions)

		const marginTop = (-47 -39 -this._properties.scrollTop);
		const addonList = <HTMLElement>document.querySelector('.addon-list');
		//addonList.style.marginTop = marginTop + 'px'
	}

	public get width():number
	{
		return 280;
	}

	public get buildingSize()
	{
		return this._buildingSize;
	}

	public get overhang()
	{
		return this._overHang;
	}

	public get scenePanel()
	{
		return this._scenePanel;
	}

	private get isInMobileMode()
	{
		return Main.getInstance().isInMobileMode;
	}
}
